import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Download, Calendar, GraduationCap, CheckCircle2, Award } from 'lucide-react';

// CognitiaIndia.com — Course Completion Certificate (Phase 39.14)
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CourseCertificate = ({ certificate, isOpen, onClose }) => {
  const [downloading, setDownloading] = useState(false);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const handleDownload = async () => {
    if (!certificate?.course_id) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/certificates/${certificate.course_id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CognitiaIndia_Certificate_${(certificate.student_name || 'Student').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      // Fallback — open a browser print view.
      const w = window.open('', '_blank');
      w?.document.write(`<pre>Certificate download failed: ${e.message}. Please try again.</pre>`);
      w?.document.close();
    } finally {
      setDownloading(false);
    }
  };

  if (!certificate) return null;
  const sig = certificate.signatories || [
    { name: 'Prasanna S Chitale', title: 'Authorized Signatory' },
    { name: 'Shrikant Digambar', title: 'Authorized Signatory' },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden" data-testid="certificate-modal">
        <DialogHeader className="sr-only">
          <DialogTitle>Course Certificate</DialogTitle>
          <DialogDescription>Certificate for {certificate.course_title}</DialogDescription>
        </DialogHeader>

        {/* Certificate Preview */}
        <div
          data-testid="certificate-preview"
          className="bg-gradient-to-br from-white via-amber-50 to-white text-slate-900 p-10 md:p-14 border-[10px] border-violet-900 relative"
        >
          {/* inner gold frame */}
          <div className="absolute inset-3 border-2 border-amber-500 pointer-events-none" />

          {/* Heading */}
          <div className="text-center mb-8 relative">
            <p
              data-testid="cert-platform-name"
              className="text-3xl md:text-4xl font-bold text-violet-900 tracking-wide"
            >
              CognitiaIndia.com
            </p>
            <p className="text-xs md:text-sm text-slate-600 mt-1 tracking-[0.2em] uppercase">
              Empowering Learners · Building Careers
            </p>
          </div>

          <div className="text-center mb-6 relative">
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-widest">
              CERTIFICATE OF COMPLETION
            </h1>
          </div>

          {/* Body */}
          <div className="text-center mb-6 relative">
            <p className="text-slate-700 text-sm md:text-base">This is to certify that</p>
          </div>

          <div className="text-center mb-6 relative">
            <h2
              data-testid="cert-student-name"
              className="text-3xl md:text-4xl font-bold text-violet-800 border-b-2 border-amber-500 inline-block pb-2 px-6"
            >
              {certificate.student_name}
            </h2>
          </div>

          <div className="text-center mb-6 relative">
            <p className="text-slate-700 text-sm md:text-base mb-2">
              has successfully completed the course
            </p>
            <h3
              data-testid="cert-course-title"
              className="text-xl md:text-2xl font-bold text-amber-700"
            >
              {certificate.course_title || 'Programming Course'}
            </h3>
          </div>

          {/* Details */}
          <div className="grid grid-cols-3 gap-4 mb-8 max-w-xl mx-auto relative">
            <div className="text-center p-3 bg-white/70 rounded-lg border border-amber-200">
              <Calendar className="w-5 h-5 mx-auto mb-1 text-violet-700" />
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Completion Date</p>
              <p className="text-slate-900 font-semibold text-sm">
                {formatDate(certificate.completion_date)}
              </p>
            </div>
            <div className="text-center p-3 bg-white/70 rounded-lg border border-amber-200">
              <Award className="w-5 h-5 mx-auto mb-1 text-green-600" />
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Exam Score</p>
              <p className="text-slate-900 font-semibold text-sm">{certificate.exam_score}%</p>
            </div>
            <div className="text-center p-3 bg-white/70 rounded-lg border border-amber-200">
              <GraduationCap className="w-5 h-5 mx-auto mb-1 text-violet-700" />
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Chapters</p>
              <p className="text-slate-900 font-semibold text-sm">{certificate.chapters_completed}</p>
            </div>
          </div>

          {/* Signatories */}
          <div className="flex justify-around items-end pt-6 relative">
            {sig.map((s, i) => (
              <div key={i} className="text-center flex-1" data-testid={`cert-signatory-${i}`}>
                <div className="italic text-slate-700 font-serif text-lg mb-1">{s.name}</div>
                <div className="border-t border-slate-800 mx-8" />
                <div className="text-xs text-slate-500 mt-2">{s.title}</div>
              </div>
            ))}
          </div>

          {/* Verification */}
          <div className="flex items-center justify-center gap-2 mt-6 relative">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-green-700 text-xs font-medium">Verified Certificate</span>
          </div>

          {/* Footer */}
          <div className="text-center pt-3 mt-4 border-t border-amber-200 relative">
            <p className="text-slate-500 text-xs">
              Certificate ID: <span className="font-mono">{certificate.certificate_id}</span>
            </p>
            <p className="text-slate-400 text-xs mt-1">https://cognitiaindia.com</p>
          </div>
        </div>

        {/* Download */}
        <div className="p-4 bg-background border-t">
          <Button
            onClick={handleDownload}
            className="w-full"
            disabled={downloading}
            data-testid="download-certificate-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? 'Preparing PDF…' : 'Download Certificate (PDF)'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CourseCertificate;
